import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageSquare, BookOpen, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface Cohort {
  id: string;
  name: string;
  description: string;
  track_name: string;
  track_slug: string;
  instructor_name: string;
  start_date: string;
  end_date: string | null;
  member_count: number;
  max_members: number | null;
}

interface StudyGroup {
  id: string;
  name: string;
  description: string;
  course_title: string;
  course_slug: string;
  creator_name: string;
  member_count: number;
  max_members: number;
}

interface Forum {
  id: string;
  name: string;
  description: string;
  thread_count: number;
  post_count: number;
}

export default function Community() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [activeTab, setActiveTab] = useState<'cohorts' | 'groups' | 'forums'>('cohorts');
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [groups, setGroups] = useState<StudyGroup[]>([]);
  const [forums, setForums] = useState<Forum[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function loadData() {
    setLoading(true);
    try {
      if (activeTab === 'cohorts') {
        const data = await fetchApi<{ cohorts: Cohort[] }>('/api/cohorts');
        setCohorts(data.cohorts);
      } else if (activeTab === 'groups') {
        const data = await fetchApi<{ groups: StudyGroup[] }>('/api/groups');
        setGroups(data.groups);
      } else if (activeTab === 'forums') {
        const data = await fetchApi<{ forums: Forum[] }>('/api/discussions/forums');
        setForums(data.forums);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  const tabs = [
    { id: 'cohorts', label: 'Cohorts', icon: Calendar },
    { id: 'groups', label: 'Study Groups', icon: Users },
    { id: 'forums', label: 'Discussions', icon: MessageSquare },
  ] as const;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Community</h1>
        <p className="text-gray-400">
          Connect with fellow learners, join cohorts, and participate in discussions.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-gray-700">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 font-medium transition-colors
                border-b-2 -mb-px
                ${activeTab === tab.id
                  ? 'text-accent-blue border-accent-blue'
                  : 'text-gray-400 border-transparent hover:text-white'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
        </div>
      ) : (
        <>
          {/* Cohorts Tab */}
          {activeTab === 'cohorts' && (
            <div className="space-y-4">
              {cohorts.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No active cohorts at the moment.</p>
                  <p className="text-sm mt-1">Check back soon for upcoming learning groups!</p>
                </div>
              ) : (
                cohorts.map(cohort => (
                  <Link
                    key={cohort.id}
                    to={`/cohorts/${cohort.id}`}
                    className="block bg-gray-800/50 rounded-lg p-6 hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-1 bg-accent-blue/20 text-accent-blue rounded">
                            {cohort.track_name}
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-1">{cohort.name}</h3>
                        <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                          {cohort.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Led by {cohort.instructor_name}</span>
                          <span>•</span>
                          <span>
                            Starts {new Date(cohort.start_date).toLocaleDateString()}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {cohort.member_count}
                            {cohort.max_members && ` / ${cohort.max_members}`}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}

          {/* Study Groups Tab */}
          {activeTab === 'groups' && (
            <div>
              {user && (
                <Link
                  to="/groups/create"
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-accent-blue hover:bg-blue-500 text-slate-900 rounded-lg transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Create Study Group
                </Link>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                {groups.length === 0 ? (
                  <div className="col-span-2 text-center py-12 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No study groups yet.</p>
                    {user && (
                      <p className="text-sm mt-1">Be the first to create one!</p>
                    )}
                  </div>
                ) : (
                  groups.map(group => (
                    <Link
                      key={group.id}
                      to={`/groups/${group.id}`}
                      className="block bg-gray-800/50 rounded-lg p-5 hover:bg-gray-800 transition-colors"
                    >
                      <h3 className="text-lg font-semibold text-white mb-1">{group.name}</h3>
                      {group.course_title && (
                        <p className="text-sm text-accent-blue mb-2">
                          <BookOpen className="w-3 h-3 inline mr-1" />
                          {group.course_title}
                        </p>
                      )}
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {group.description || 'No description'}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>Created by {group.creator_name}</span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {group.member_count}/{group.max_members}
                        </span>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Forums Tab */}
          {activeTab === 'forums' && (
            <div className="space-y-4">
              {forums.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No discussion forums available.</p>
                </div>
              ) : (
                forums.map(forum => (
                  <Link
                    key={forum.id}
                    to={`/forums/${forum.id}`}
                    className="block bg-gray-800/50 rounded-lg p-5 hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-1">{forum.name}</h3>
                        <p className="text-gray-400 text-sm">{forum.description}</p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>{forum.thread_count} threads</p>
                        <p>{forum.post_count} posts</p>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
